import { Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { AuthService } from 'src/app/services/auth.service';
import { ToasterService } from 'src/app/services/snackbar.service';
import { SupportTicketService } from 'src/app/services/support-ticket.service';
import {
  SUPPORT_TICKET_MAX_DESCRIPTION_LENGTH,
  SUPPORT_TICKET_MAX_FILE_SIZE,
} from './support-ticket.constants';

@Component({
  selector: 'app-support-ticket-create',
  templateUrl: './support-ticket-create.component.html',
  styleUrls: ['./support-ticket-create.component.scss'],
})
export class SupportTicketCreateComponent implements OnInit, OnDestroy {
  readonly maxIssueLength = 150;
  readonly maxDescriptionLength = SUPPORT_TICKET_MAX_DESCRIPTION_LENGTH;
  readonly maxFileSize = SUPPORT_TICKET_MAX_FILE_SIZE;

  ticketForm: FormGroup;
  selectedFile: File | null = null;
  isDragOver = false;
  fileError = '';
  submitting = false;
  uploadInProgress = false;
  showCancelConfirmation = false;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly router: Router,
    private readonly authService: AuthService,
    private readonly supportTicketService: SupportTicketService,
    private readonly toasterService: ToasterService
  ) {}

  ngOnInit(): void {
    this.ticketForm = this.formBuilder.group({
      issue: ['', [SupportTicketCreateComponent.requiredTrimmedValue, Validators.maxLength(this.maxIssueLength)]],
      description: [
        '',
        [
          SupportTicketCreateComponent.requiredTrimmedValue,
          Validators.maxLength(this.maxDescriptionLength),
        ],
      ],
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get descriptionControl(): AbstractControl | null {
    return this.ticketForm.get('description');
  }

  get issueControl(): AbstractControl | null {
    return this.ticketForm.get('issue');
  }

  get descriptionLength(): number {
    return this.descriptionControl?.value?.length || 0;
  }

  get selectedFileSizeLabel(): string {
    if (!this.selectedFile) {
      return '';
    }

    if (this.selectedFile.size >= 1024 * 1024) {
      return `${(this.selectedFile.size / (1024 * 1024)).toFixed(2)} MB`;
    }

    return `${Math.ceil(this.selectedFile.size / 1024)} KB`;
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      void this.setFile(input.files[0], input);
    }
  }

  onFileDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;

    if (event.dataTransfer?.files?.length) {
      void this.setFile(event.dataTransfer.files[0]);
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;
  }

  removeFile(fileInput?: HTMLInputElement, event?: Event): void {
    event?.stopPropagation();
    this.selectedFile = null;
    this.fileError = '';

    if (fileInput) {
      fileInput.value = '';
    }
  }

  cancel(): void {
    if (!this.hasEnteredValidData()) {
      this.router.navigate(['/support-tickets']);
      return;
    }

    this.showCancelConfirmation = true;
  }

  closeCancelConfirmation(): void {
    this.showCancelConfirmation = false;
  }

  confirmCancel(): void {
    this.showCancelConfirmation = false;
    this.router.navigate(['/support-tickets']);
  }

  submit(): void {
    if (this.ticketForm.invalid) {
      this.ticketForm.markAllAsTouched();
      return;
    }

    const userId = this.getCurrentUserId();
    if (!userId) {
      this.toasterService.openSnackBar(
        { type: 'error', msg: 'Unable to identify the current user. Please log in again.' },
        'Dismiss'
      );
      return;
    }

    this.submitting = true;
    this.uploadInProgress = !!this.selectedFile;

    const request$ = this.supportTicketService.submitTicket(
      userId,
      this.descriptionControl?.value,
      this.selectedFile,
      this.issueControl?.value?.trim()
    );

    request$
      .pipe(
        finalize(() => {
          this.submitting = false;
          this.uploadInProgress = false;
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: () => {
          this.toasterService.openSnackBar(
            { type: 'success', msg: 'Support ticket submitted successfully.' },
            ''
          );
          this.router.navigate(['/support-tickets']);
        },
        error: () => {
          this.toasterService.openSnackBar(
            { type: 'error', msg: 'Unable to submit support ticket. Please try again.' },
            'Dismiss'
          );
        },
      });
  }

  private async setFile(file: File, fileInput?: HTMLInputElement): Promise<void> {
    this.fileError = '';

    if (!(await this.isPdfFile(file))) {
      this.selectedFile = null;
      this.fileError = 'Only PDF files are allowed.';
      this.resetFileInput(fileInput);
      return;
    }

    if (file.size > this.maxFileSize) {
      this.selectedFile = null;
      this.fileError = 'File size must be 5 MB or less.';
      this.resetFileInput(fileInput);
      return;
    }

    this.selectedFile = file;
  }

  private async isPdfFile(file: File): Promise<boolean> {
    const fileName = file.name.toLowerCase();
    const hasPdfNameOrMime = file.type === 'application/pdf' || fileName.endsWith('.pdf');

    if (!hasPdfNameOrMime) {
      return false;
    }

    try {
      const headerBuffer = await file.slice(0, 5).arrayBuffer();
      const headerBytes = new Uint8Array(headerBuffer);

      return headerBytes.length === 5
        && headerBytes[0] === 0x25
        && headerBytes[1] === 0x50
        && headerBytes[2] === 0x44
        && headerBytes[3] === 0x46
        && headerBytes[4] === 0x2d;
    } catch {
      return false;
    }
  }

  private resetFileInput(fileInput?: HTMLInputElement): void {
    if (fileInput) {
      fileInput.value = '';
    }
  }

  private getCurrentUserId(): string | null {
    const userId = this.authService.userValue?.id || this.authService.getCurrentUser()?.id;
    return userId ? String(userId) : null;
  }

  private hasEnteredValidData(): boolean {
    return this.hasValidTextValue(this.issueControl)
      || this.hasValidTextValue(this.descriptionControl)
      || !!this.selectedFile;
  }

  private hasValidTextValue(control: AbstractControl | null): boolean {
    const value = typeof control?.value === 'string' ? control.value.trim() : '';
    return !!value && !!control?.valid;
  }

  private static requiredTrimmedValue(control: AbstractControl): { required: true } | null {
    const value = control.value;

    if (typeof value === 'string' && !value.trim()) {
      return { required: true };
    }

    return value ? null : { required: true };
  }
}